import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { KizenTypography } from '__app/typography';
import {
  TableTitleWrapper,
  TableBoxesWrapper,
  SearchContainer,
} from '__pages/Account/pages/Profile/Forms/tableStyles';
import ProfileInfiniteTable from 'pages/Account/pages/Profile/Forms/ProfileInfiniteTable';
import ConfirmationModal from '__components/Modals/presets/ConfirmationModal';
import { PageSearchInput } from 'components/Layout/PageToolbar';
import { useAccessTokens } from 'hooks/accessTokens/useAccessTokens';
import { createColumns } from 'pages/Account/pages/Profile/Forms/OAuth/columns';
import { APIKeysNotice } from 'pages/Account/pages/Profile/Forms/OAuth/APIKeysNotice';
import {
  ScrollContainerBlocker,
  ScrollContainerBlockerWrapper,
} from 'components/Tables/ScrollContainerStyles';
import type { AccessStruct } from '@kizen/permissions/types';

const APPLICATION_WIDTH = '160px';
const TEAM_MEMBER_WIDTH = '155px';
const STATUS_WIDTH = '65px';
const REFRESH_WIDTH = '120px';
const ACTION_WIDTH = '51px';
const PADDING_WIDTH = '20px';

const columnWidths = {
  application: APPLICATION_WIDTH,
  employee: TEAM_MEMBER_WIDTH,
  status: STATUS_WIDTH,
  created: REFRESH_WIDTH,
  actions: ACTION_WIDTH,
  padding: PADDING_WIDTH,
};

export const OAuth: FC<{
  access: AccessStruct;
}> = ({ access }) => {
  const { t } = useTranslation();

  const {
    handleTokenRevoke: onTokenRevoke,
    accessTokens,
    oauthTokensModalProps,
    messageHeadData,
    messagesSort,
    setMessagesSort,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    status,
    search,
    setSearch,
    anyAccessTokens,
  } = useAccessTokens(null);

  const columns = useMemo(
    () =>
      createColumns({
        onSelectAction: ({ value }: { value: string }, key: { id: string }) => {
          const { id } = key;
          if (value === 'onTokenRevoke') {
            onTokenRevoke(id);
          }
        },
        columnWidths,
        canRevoke: access.remove,
        t,
      }),
    [onTokenRevoke, access, t]
  );

  return (
    <>
      <TableTitleWrapper marginBottom="11">
        <KizenTypography as="h3" type="subheader">
          {t('OAuth Authorizations')}
        </KizenTypography>
        <SearchContainer>
          <PageSearchInput
            placeholder={t('Find Authorizations')}
            value={search}
            onChange={setSearch}
            loading={isFetchingNextPage || isFetching}
          />
        </SearchContainer>
      </TableTitleWrapper>
      <TableBoxesWrapper>
        <APIKeysNotice>
          <KizenTypography as="div">
            {t(
              `(For Techies) A list of OAuth authorizations established for all team members in this business are enumerated below. You can revoke tokens, based on your permission level.`
            )}
            {!anyAccessTokens
              ? t(` There are not yet any authorizations.`)
              : null}
          </KizenTypography>
        </APIKeysNotice>
      </TableBoxesWrapper>
      {accessTokens ? (
        <>
          <ScrollContainerBlockerWrapper className="scroll-container-blocker-wrapper">
            <ScrollContainerBlocker />
          </ScrollContainerBlockerWrapper>
          <ProfileInfiniteTable
            columns={columns}
            data={accessTokens}
            messageHeadData={messageHeadData}
            messagesSort={messagesSort}
            setMessagesSort={setMessagesSort}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            isFetching={isFetching}
            status={status}
          />
        </>
      ) : (
        false
      )}
      <ConfirmationModal
        buttonText={t('Confirm')}
        heading={t('Please Confirm Revocation')}
        {...oauthTokensModalProps}
      >
        {t(
          'Revoking this authorization will make all subsequent requests using the token invalid and denied by Kizen.'
        )}
      </ConfirmationModal>
    </>
  );
};
